import { useIsMobile } from "Hooks/useIsMobile";
import { useState, useRef, type TouchEvent, type MouseEvent } from "react";

type UseSwipeToDismissProps = {
  onDismiss?: () => void;
  mode?: "swipe" | "click" | "disabled";
  maxDragDistance?: number;
  dismissThreshold?: number;
  fadeStartDistance?: number;
  slideDistance?: number;
  animationDuration?: number;
};

type UseSwipeToDismissReturn = {
  handleDismiss: () => void;
  draggedItemStyles:
    | {
        opacity: number;
        transform: `translateX(${number}px)`;
        transition: string;
        userSelect: "none";
        touchAction: "pan-y";
      }
    | Record<string, never>;
  dragEventHandlers:
    | {
        onTouchStart: (e: TouchEvent) => void;
        onTouchMove: (e: TouchEvent) => void;
        onTouchEnd: () => void;
        onMouseDown: (e: MouseEvent) => void;
        onMouseMove: (e: MouseEvent) => void;
        onMouseUp: () => void;
        onMouseLeave: () => void;
      }
    | Record<string, never>;
};

export const useSwipeToDismiss = ({
  onDismiss = () => {},
  mode = "swipe",
  maxDragDistance: maxDragDistanceProp,
  dismissThreshold = 100,
  fadeStartDistance = 1,
  slideDistance = 300,
  animationDuration = 300,
}: UseSwipeToDismissProps): UseSwipeToDismissReturn => {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isMobile = useIsMobile();
  const maxDragDistance = maxDragDistanceProp || (isMobile ? 350 : 600);

  const handleTouchStart = (e: TouchEvent) => {
    setIsDragging(true);
    startX.current = e.touches[0].clientX;
    currentX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) {
      return;
    }

    currentX.current = e.touches[0].clientX;
    const deltaX = currentX.current - startX.current;

    // Allow swiping in both directions
    if (Math.abs(deltaX) > 0) {
      setTranslateX(
        Math.max(-maxDragDistance, Math.min(deltaX, maxDragDistance)),
      );
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) {
      return;
    }

    setIsDragging(false);

    const deltaX = currentX.current - startX.current;

    // If swiped more than threshold in either direction, trigger close animation
    if (Math.abs(deltaX) > dismissThreshold) {
      setIsClosing(true);
      // Animate to full width in the direction of swipe and fade out
      const slideDirection = deltaX > 0 ? slideDistance : -slideDistance;
      setTranslateX(slideDirection);
      setTimeout(() => {
        onDismiss();
      }, animationDuration);
    } else {
      // Reset position smoothly
      setTranslateX(0);
    }
  };

  const handleMouseDown = (e: MouseEvent) => {
    setIsDragging(true);
    startX.current = e.clientX;
    currentX.current = e.clientX;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) {
      return;
    }

    currentX.current = e.clientX;
    const deltaX = currentX.current - startX.current;

    // Allow swiping in both directions
    if (Math.abs(deltaX) > 0) {
      setTranslateX(
        Math.max(-maxDragDistance, Math.min(deltaX, maxDragDistance)),
      );
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) {
      return;
    }

    setIsDragging(false);

    const deltaX = currentX.current - startX.current;

    // If swiped more than threshold in either direction, trigger close animation
    if (Math.abs(deltaX) > dismissThreshold) {
      setIsClosing(true);
      // Animate to full width in the direction of swipe and fade out
      const slideDirection = deltaX > 0 ? slideDistance : -slideDistance;
      setTranslateX(slideDirection);
      setTimeout(() => {
        onDismiss();
      }, animationDuration);
    } else {
      // Reset position smoothly
      setTranslateX(0);
    }
  };

  const handleMouseLeave = () => {
    handleMouseUp();
  };

  const handleDismiss = () => {
    setIsClosing(true);
    setTranslateX(slideDistance);
    setTimeout(() => {
      onDismiss();
    }, animationDuration);
  };

  // Calculate opacity based on drag distance (fade out as dragged further in either direction)
  const absTranslateX = Math.abs(translateX);
  const opacity = isClosing
    ? 0
    : absTranslateX > fadeStartDistance
      ? Math.max(
          0,
          1 -
            ((absTranslateX - fadeStartDistance) /
              (maxDragDistance - fadeStartDistance)) *
              0.9,
        )
      : 1;

  const draggedItemStyles = {
    opacity,
    transform: `translateX(${translateX}px)`,
    transition: isDragging
      ? "opacity 0.1s ease-out"
      : isClosing
        ? `transform ${animationDuration}ms ease-out, opacity ${animationDuration}ms ease-out`
        : "transform 0.2s ease-out, opacity 0.1s ease-out",
    userSelect: "none" as const,
    touchAction: "pan-y" as const,
  } as const;

  if (mode === "disabled") {
    return {
      handleDismiss: () => {},
      dragEventHandlers: {},
      draggedItemStyles: {},
    };
  }

  if (mode === "click") {
    return {
      handleDismiss,
      draggedItemStyles, //to still show the dismiss animation
      dragEventHandlers: {},
    };
  }

  return {
    handleDismiss,
    draggedItemStyles,
    dragEventHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
    },
  };
};
